import React, { memo, useCallback, useMemo } from 'react'
import { TableColumnItem } from '../../model/types/types'
import { setJsonSettings } from '../../../../../store/session/actions'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { saveJsonSettings } from '../../../../../store/session/action-creators'
import { getJsonSettingsByKey } from '../../model/selectors/store'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'
import {
  SortableItemsSelector,
  SortableItem,
} from '../../../../../components/sortable-items-selector'
import { getColumnOrder } from '../../utils/getColumnOrder'
import { getMainColumnKeys } from '../../model/default-columns'

interface TableSettingsModalProps {
  className?: string
  isOpen?: boolean
  onCancel?: () => void
  items: TableColumnItem[]
  settingsKey?: string
  mainKeys?: string[]
  initialKeys?: string[]
  allColumnsLabel?: string
}

const TableSettingsModal: React.FC<TableSettingsModalProps> = props => {
  const {
    isOpen,
    onCancel,
    items,
    settingsKey,
    mainKeys,
    initialKeys,
    allColumnsLabel = 'Все колонки',
  } = props
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const key = settingsKey || pathname
  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, key))

  const sortableItems: SortableItem[] = useMemo(
    () =>
      items
        .filter((item, index) => {
          if (!item.key || items.findIndex(other => other.key === item.key) !== index) return false
          if (!item.title) return false
          if (typeof item.title !== 'string') return true
          return item.title.trim() !== ''
        })
        .map(item => ({
          key: item.key,
          title: item.title,
        })),
    [items],
  )

  const selectedKeys = useMemo((): string[] => {
    const available = sortableItems.map(item => item.key)
    const saved = getColumnOrder(jsonSettings).filter(item => available.includes(item))
    if (saved.length) return saved
    const initial = initialKeys?.filter(item => available.includes(item))
    return initial?.length ? initial : available
  }, [jsonSettings, sortableItems, initialKeys])

  const presets = useMemo(() => {
    const all = sortableItems.map(item => item.key)
    const main = mainKeys?.filter(item => all.includes(item)) || getMainColumnKeys(key, all)
    return [
      { key: 'main', title: 'Основные колонки', selectedKeys: main.length ? main : all },
      { key: 'all', title: allColumnsLabel, selectedKeys: all },
    ]
  }, [sortableItems, mainKeys, key, allColumnsLabel])

  const handleSave = useCallback(
    (selectedKeys: string[]) => {
      if (!selectedKeys.length) return
      const currentSettings = jsonSettings || {}
      const widths = Array.isArray(currentSettings) ? {} : currentSettings.widths || {}

      dispatch(
        setJsonSettings({
          [key]: {
            ...(Array.isArray(currentSettings) ? {} : currentSettings),
            order: selectedKeys,
            widths: widths,
          },
        }),
      )
      dispatch(saveJsonSettings())
      onCancel?.()
    },
    [jsonSettings, key, dispatch, onCancel],
  )

  return (
    <SortableItemsSelector
      visible={isOpen || false}
      title="Настройка колонок"
      presets={presets}
      availableItems={sortableItems}
      selectedItemKeys={selectedKeys}
      leftPanelTitle="Скрытые столбцы"
      rightPanelTitle="Видимые столбцы"
      emptyLeftText="Все столбцы добавлены"
      emptyRightText="Выберите столбцы для отображения"
      emptyRightDescription="Нажмите на столбец для добавления"
      showSearch={true}
      width={1000}
      onOk={handleSave}
      onCancel={() => onCancel?.()}
    />
  )
}

export default memo(TableSettingsModal)
